import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  LogOut, DollarSign, Package, ShoppingCart, CheckCircle2, Clock, Store, ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) {
  if (n == null) return '$0';
  return '$' + Number(n).toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

export default function ReceptionistDashboard() {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'recepcionista') {
      navigate('/login');
      return;
    }
    fetchCommissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchCommissions = async () => {
    try {
      const res = await axios.get(`${API}/users/me/commissions`, getAuthHeaders());
      setData(res.data);
    } catch (e) {
      toast.error('Error al cargar comisiones');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <p className="text-zinc-500">Cargando...</p>
      </div>
    );
  }

  const items = data?.items || [];

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="receptionist-dashboard">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div>
            <p className="text-xs text-zinc-500 uppercase tracking-widest">Recepción</p>
            <h1 className="text-xl font-semibold text-white" data-testid="recep-name">{user?.name}</h1>
            <p className="text-xs text-zinc-500">
              {user?.branch_names?.length > 0
                ? user.branch_names.join(' · ')
                : 'Sin sucursales asignadas'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => navigate('/pos')}
              className="bg-gold hover:bg-gold/90 text-black"
              data-testid="go-to-pos-btn"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Ir al POS
              <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
            <Button onClick={logout} variant="ghost" className="text-zinc-400 hover:text-white" data-testid="recep-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="bg-gradient-to-br from-gold/10 to-zinc-900/50 border-gold/30 p-5" data-testid="recep-month-total">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign className="w-4 h-4 text-gold" />
              <span className="text-xs text-gold uppercase tracking-wider">Mes Actual</span>
            </div>
            <p className="text-3xl font-bold text-white">{fmt(data?.month_total)}</p>
            <p className="text-xs text-zinc-500 mt-1">Comisiones generadas</p>
          </Card>

          <Card className="bg-zinc-900/50 border-yellow-900/40 p-5" data-testid="recep-pending-total">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-yellow-400" />
              <span className="text-xs text-yellow-400 uppercase tracking-wider">Pendiente de Pago</span>
            </div>
            <p className="text-3xl font-bold text-yellow-300">{fmt(data?.pending_total)}</p>
            <p className="text-xs text-zinc-500 mt-1">Por cobrar</p>
          </Card>

          <Card className="bg-zinc-900/50 border-green-900/40 p-5" data-testid="recep-paid-total">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle2 className="w-4 h-4 text-green-400" />
              <span className="text-xs text-green-400 uppercase tracking-wider">Pagado</span>
            </div>
            <p className="text-3xl font-bold text-green-300">{fmt(data?.paid_total)}</p>
            <p className="text-xs text-zinc-500 mt-1">Histórico</p>
          </Card>
        </div>

        {/* Commissions list */}
        <Card className="bg-zinc-900/50 border-zinc-800 p-5" data-testid="recep-commissions-list">
          <h3 className="text-sm text-zinc-400 mb-4 flex items-center gap-2">
            <Package className="w-4 h-4 text-blue-400" />
            Historial de Comisiones por Producto
          </h3>
          {items.length === 0 ? (
            <div className="text-center py-10">
              <Package className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-500 text-sm">Aún no tienes comisiones registradas</p>
              <p className="text-zinc-600 text-xs mt-1">Vende productos en el POS para acumular comisiones</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800">
                    <th className="text-left text-zinc-500 py-2 px-3 font-medium">Fecha</th>
                    <th className="text-left text-zinc-500 py-2 px-3 font-medium">Producto</th>
                    <th className="text-right text-zinc-500 py-2 px-3 font-medium">Venta</th>
                    <th className="text-right text-zinc-500 py-2 px-3 font-medium">Tasa</th>
                    <th className="text-right text-zinc-500 py-2 px-3 font-medium">Comisión</th>
                    <th className="text-center text-zinc-500 py-2 px-3 font-medium">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c) => (
                    <tr key={c.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30" data-testid={`recep-comm-row-${c.id}`}>
                      <td className="py-3 px-3 text-zinc-300 text-xs">
                        {new Date(c.created_at).toLocaleDateString('es-MX')}
                      </td>
                      <td className="py-3 px-3 text-zinc-200">{c.item_name}</td>
                      <td className="text-right py-3 px-3 text-zinc-400">{fmt(c.price)}</td>
                      <td className="text-right py-3 px-3 text-zinc-500">{c.commission_rate || 0}%</td>
                      <td className="text-right py-3 px-3 text-blue-400 font-semibold">{fmt(c.commission)}</td>
                      <td className="text-center py-3 px-3">
                        {c.paid ? (
                          <span className="text-xs px-2 py-0.5 rounded bg-green-500/10 text-green-400">Pagada</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded bg-yellow-500/10 text-yellow-400">Pendiente</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <p className="text-[10px] text-zinc-600 text-center">
          Las comisiones se calculan automáticamente al cerrar una venta de productos en el POS.
        </p>
      </main>
    </div>
  );
}
